import React from "react";
import {
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Box,
  Button,
  alpha,
  useTheme,
  Alert,
  Stack,
} from "@mui/material";
import {useAuth0} from "@auth0/auth0-react";
import {useStateContext} from "contexts/userContext";
import {useNavigate} from "react-router-dom";
import {useTranslation} from "react-i18next";
import RestaurantMenuIcon from "@mui/icons-material/RestaurantMenu";
import ReceiptIcon from "@mui/icons-material/Receipt";
import BarChartIcon from "@mui/icons-material/BarChart";
import SettingsIcon from "@mui/icons-material/Settings";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

export function Home() {
  const {isAuthenticated, user} = useAuth0();
  const {restaurant_id, settings} = useStateContext();
  const {t} = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();

  // Get business name from settings or use fallback with restaurant_id
  const displayName = settings?.businessName || (restaurant_id ? `business_${restaurant_id}` : user?.nickname || user?.name);

  const quickActions = [
    {
      title: t("Manage Menu"),
      description: t("Add, edit, or remove dishes from your menu"),
      icon: RestaurantMenuIcon,
      color: "#2563eb",
      path: "/dishes-new",
    },
    {
      title: t("View Orders"),
      description: t("Check and manage incoming orders"),
      icon: ReceiptIcon,
      color: "#8b5cf6",
      path: "/orders",
    },
    {
      title: t("Analytics"),
      description: t("View insights and performance metrics"),
      icon: BarChartIcon,
      color: "#10b981",
      path: "/statistics",
    },
    {
      title: t("QR Codes"),
      description: t("Generate and download QR codes"),
      icon: QrCode2Icon,
      color: "#f59e0b",
      path: "/qr-code-generator",
    },
  ];

  const stats = [
    {label: t("Today's Orders"), value: "0", trend: "+0%", color: "#2563eb"},
    {label: t("Revenue"), value: "$0", trend: "+0%", color: "#10b981"},
    {label: t("Menu Items"), value: "0", trend: "—", color: "#8b5cf6"},
  ];

  if (isAuthenticated) {
    return (
      <Box sx={{p: {xs: 2, md: 4}, bgcolor: "background.default", minHeight: "100vh"}}>
        {/* Welcome Header */}
        <Box sx={{mb: 4}}>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 800,
              mb: 1,
              color: "text.primary",
              fontSize: {xs: "2rem", md: "2.5rem"},
            }}
          >
            {t("Hi")}, {displayName}! 👋
          </Typography>
          <Typography variant="body1" sx={{color: "text.secondary", fontSize: "1.125rem"}}>
            {t("Here's what's happening with your business today")}
          </Typography>
        </Box>

        {/* Setup Warning */}
        {!restaurant_id && (
          <Alert
            severity="warning"
            sx={{
              mb: 4,
              borderRadius: "12px",
              border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
            }}
          >
            {t("Setting up your restaurant... If this takes too long, please make sure the backend server is running.")}
          </Alert>
        )}

        {/* Stats Grid */}
        <Grid container spacing={3} sx={{mb: 4}}>
          {stats.map((stat, index) => (
            <Grid item xs={12} sm={4} key={index}>
              <Card
                sx={{
                  height: "100%",
                  background: `linear-gradient(135deg, ${alpha(stat.color, 0.05)} 0%, ${alpha(
                    stat.color,
                    0.02
                  )} 100%)`,
                  border: `1px solid ${alpha(stat.color, 0.2)}`,
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: `0 8px 24px ${alpha(stat.color, 0.15)}`,
                  },
                }}
              >
                <CardContent>
                  <Typography variant="body2" sx={{color: "text.secondary", mb: 1}}>
                    {stat.label}
                  </Typography>
                  <Box sx={{display: "flex", alignItems: "baseline", gap: 1}}>
                    <Typography
                      variant="h4"
                      sx={{fontWeight: 700, color: "text.primary"}}
                    >
                      {stat.value}
                    </Typography>
                    {stat.trend !== "—" && (
                      <Typography
                        variant="body2"
                        sx={{
                          color: "success.main",
                          fontWeight: 600,
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                        }}
                      >
                        <TrendingUpIcon sx={{fontSize: 16}} />
                        {stat.trend}
                      </Typography>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Quick Actions */}
        <Typography variant="h5" sx={{fontWeight: 700, mb: 3, color: "text.primary"}}>
          {t("Quick Actions")}
        </Typography>
        <Grid container spacing={3} sx={{mb: 4}}>
          {quickActions.map((action, index) => {
            const IconComponent = action.icon;
            return (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card
                  sx={{
                    height: "100%",
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-6px)",
                      boxShadow: `0 12px 28px ${alpha(action.color, 0.15)}`,
                      borderColor: action.color,
                    },
                  }}
                  onClick={() => navigate(action.path)}
                >
                  <CardContent>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: "12px",
                        bgcolor: alpha(action.color, 0.1),
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mb: 2,
                      }}
                    >
                      <IconComponent sx={{fontSize: 24, color: action.color}} />
                    </Box>
                    <Typography variant="h6" sx={{fontWeight: 700, mb: 1}}>
                      {action.title}
                    </Typography>
                    <Typography variant="body2" sx={{color: "text.secondary"}}>
                      {action.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>

        {/* Getting Started Guide */}
        <Card sx={{mb: 4}}>
          <CardContent sx={{p: 4}}>
            <Typography variant="h5" sx={{fontWeight: 700, mb: 3}}>
              {t("Getting Started with QRseller")}
            </Typography>
            <Stack spacing={3}>
              {[
                {
                  step: "1",
                  title: t("Set up your menu"),
                  description: t("Add your dishes, prices, and photos to create an attractive digital menu"),
                  action: t("Go to Menu"),
                  path: "/dishes-new",
                },
                {
                  step: "2",
                  title: t("Generate QR codes"),
                  description: t("Create QR codes for tables, checkout, or anywhere customers can scan"),
                  action: t("Get QR Codes"),
                  path: "/qrcodes",
                },
                {
                  step: "3",
                  title: t("Start receiving orders"),
                  description: t("Monitor orders in real-time and manage them efficiently"),
                  action: t("View Orders"),
                  path: "/orders",
                },
              ].map((item) => (
                <Box
                  key={item.step}
                  sx={{
                    display: "flex",
                    gap: 3,
                    p: 3,
                    borderRadius: "12px",
                    bgcolor: alpha(theme.palette.primary.main, 0.02),
                    border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                  }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "50%",
                      bgcolor: "primary.main",
                      color: "white",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: 700,
                      flexShrink: 0,
                    }}
                  >
                    {item.step}
                  </Box>
                  <Box sx={{flexGrow: 1}}>
                    <Typography variant="h6" sx={{fontWeight: 700, mb: 0.5}}>
                      {item.title}
                    </Typography>
                    <Typography variant="body2" sx={{color: "text.secondary", mb: 2}}>
                      {item.description}
                    </Typography>
                    <Button
                      variant="text"
                      endIcon={<ArrowForwardIcon />}
                      onClick={() => navigate(item.path)}
                      sx={{p: 0, fontWeight: 600}}
                    >
                      {item.action}
                    </Button>
                  </Box>
                </Box>
              ))}
            </Stack>
          </CardContent>
        </Card>

        {/* Help Section */}
        <Card
          sx={{
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(
              theme.palette.secondary.main,
              0.05
            )} 100%)`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
          }}
        >
          <CardContent sx={{p: 4, textAlign: "center"}}>
            <Typography variant="h5" sx={{fontWeight: 700, mb: 2}}>
              {t("Need Help?")}
            </Typography>
            <Typography variant="body1" sx={{color: "text.secondary", mb: 3}}>
              {t("Our support team is available 24/7 to help you succeed")}
            </Typography>
            <Stack direction={{xs: "column", sm: "row"}} spacing={2} justifyContent="center">
              <Button
                variant="contained"
                href="mailto:pixelmegabit@gmail.com"
                sx={{px: 4}}
              >
                {t("Email Support")}
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate("/settings")}
                startIcon={<SettingsIcon />}
              >
                {t("View Settings")}
              </Button>
            </Stack>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return null;
}